import {Injectable, signal} from '@angular/core';
import {AssignmentService} from './AssignmentService';
import {BcGqlService, IGQLAdditionalChainSettingsInfo, IGQLResponse, IGQLUserPosition} from '@betterchains/bc-gql';
import {first} from 'rxjs';
import {HttpResponse} from '@angular/common/http';

export interface ILoadedPositions {
  positions: IGQLUserPosition[]
  view_positions: IGQLUserPosition[]
  additional: IGQLAdditionalChainSettingsInfo
}

export interface ICachedPositions {
  chain_id: number,
  branch_id: number,
  positions: ILoadedPositions,
  changed: boolean
}

@Injectable({providedIn: 'root'})
export class PositionsService {

  public loading = signal(false)

  private cached: ICachedPositions[] = []

  constructor(
    private asvc: AssignmentService,
    private gql: BcGqlService,
  ) {
  }

  public Load() {
    return new Promise<ILoadedPositions>((resolve, reject) => {

      const branchId = this.asvc.GetCurrentBranchId()
      const chainId = this.asvc.GetCurrentChainId()
      const formId = this.asvc.formId;

      if (!this.asvc.IsChainAssigned() || !this.asvc.IsBranchAssigned()) {
        return;
      }

      const i = this.cached.findIndex(lf => lf.chain_id == chainId && lf.branch_id == branchId);
      if (i >= 0) {
        resolve(this.cached[i].positions);
        return;
      }

      this.loading.set(true)

      this.gql.query(
        //language=gql
        `
          query GetPositions($chainId: Int, $branchId: Int, $formId: Int) {
            forms {
              form_positions(form_id: $formId, chain_id: $chainId, branch_id: $branchId) {
                id
                name
              }
              form_additional( chain_id: $chainId, branch_id: $branchId, form_id: $formId,) {
                additional_info
                show_in_target_branch
              }
              view_positions(chain_id: $chainId, branch_id: $branchId, form_id: $formId) {
                id
                name
              }
            }
          }
        `,{chainId, branchId, formId}
      ).pipe(first()).subscribe({
        next: (r:HttpResponse<IGQLResponse<{
          forms: {
            form_positions: IGQLUserPosition[],
            form_additional: IGQLAdditionalChainSettingsInfo,
            view_positions: IGQLUserPosition[]
          }
        }>>) => {
          let additional = r.body.data.forms.form_additional
          if(!additional) {
            additional = {
              id: 0,
              form_id: formId,
              chain_id: chainId,
              branch_id: branchId,
              additional_info: "",
              show_in_target_branch: 0
            }
          }
          const loaded = {
            positions: r.body.data.forms.form_positions,
            view_positions: r.body.data.forms.view_positions,
            additional
          }
          this.cached.push({
            chain_id: chainId,
            branch_id: branchId,
            positions: loaded,
            changed: false
          })
          resolve(loaded);
          this.loading.set(false)
        }
      })

    })
  }

  public ValidateShowInTargetBranch(): boolean {
    return this.cached.every(cachedItem =>
      !cachedItem.positions.additional.show_in_target_branch || cachedItem.positions.view_positions.length > 0
    );
  }

  public Sync(data: ILoadedPositions) {
    const branchId = this.asvc.GetCurrentBranchId()
    const chainId = this.asvc.GetCurrentChainId()
    const cache = this.cached.find(lf => lf.chain_id == chainId && lf.branch_id == branchId);
    if(cache) {
      cache.positions = data;
      cache.changed = true;
    }
  }

  public Save() {
    this.cached.forEach(cachedItem => {
      if(cachedItem.changed) {
        this.gql.query(
          //language=gql
          `
          mutation SetPositions(
            $chainId: Int,
            $branchId: Int,
            $formId: Int,
            $positions: [Int],
            $viewPositions: [Int],
            $additional_info: String,
            $show_in_target_branch: Int
          ) {
              forms {
                additional(
                  chain_id: $chainId,
                  branch_id: $branchId,
                  form_id: $formId,
                  additional_info: $additional_info
                  show_in_target_branch: $show_in_target_branch
                ) {
                  id
                }
                position(chain_id: $chainId, branch_id: $branchId, form_id: $formId, positions: $positions, action: UPD) {
                  position_id
                }
                view_position(chain_id: $chainId, branch_id: $branchId, form_id: $formId, positions: $viewPositions, action: UPD) {
                  position_id
                }
              }
            }
          `,
          {
            chainId: cachedItem.chain_id,
            branchId: cachedItem.branch_id,
            formId: this.asvc.formId,
            positions: cachedItem.positions.positions.map(p => p.id),
            viewPositions: cachedItem.positions.view_positions.map(p => p.id),
            additional_info: cachedItem.positions.additional.additional_info,
            show_in_target_branch: cachedItem.positions.additional.show_in_target_branch ? 1 : 0,
          }
        ).pipe(first()).subscribe({
          next: () => {
            cachedItem.changed = false;
          }
        })
      }
    })
  }

}
